import type { Difficulty, MCQQuestion, FillQuestion, GeneratedQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function fmt2(n: number): string {
  return (Math.round(n * 100) / 100).toFixed(2);
}

/**
 * N-4-6：兩位小數的加減計算與比較。
 * 四上第六章：百分位（0.01）概念、兩位小數加減直式。
 *
 * difficulty 1：0.0x + 0.0y 或比較（純百分位）
 * difficulty 2：x.yz + x.yz 加減（帶整數部分）
 * difficulty 3：多步驟，結果可達 99.99
 */
export function generateDecimal2Place(seed: number, difficulty: Difficulty): GeneratedQuestion {
  const rand = mulberry32(seed);
  const qType = randInt(rand, 0, 2); // 0: add, 1: sub, 2: fill/compare

  if (qType === 2 && difficulty >= 2) {
    // Fill: what is the hundredths digit?
    const n = difficulty === 2
      ? randInt(rand, 100, 9999) / 100
      : randInt(rand, 1000, 99999) / 100;
    const hundredths = Math.round(n * 100) % 10;
    return {
      kind: 'fill',
      prompt: `${fmt2(n)} 的百分位數字是多少？`,
      correctAnswer: String(hundredths),
      acceptedAnswers: [String(hundredths)],
      explanation: `${fmt2(n)} 的百分位（小數第二位）是 ${hundredths}。`,
      kp: 'math.decimal.2place.4up',
    } satisfies FillQuestion;
  }

  if (qType === 0) {
    // Addition
    let a: number, b: number;
    if (difficulty === 1) {
      a = randInt(rand, 1, 9) / 100;
      b = randInt(rand, 1, 9) / 100;
    } else if (difficulty === 2) {
      a = randInt(rand, 101, 599) / 100;
      b = randInt(rand, 101, 399) / 100;
    } else {
      a = randInt(rand, 500, 5999) / 100;
      b = randInt(rand, 500, 4999) / 100;
    }
    const sum = Math.round((a + b) * 100) / 100;
    const correct = fmt2(sum);
    const w1 = fmt2(sum + 0.01);
    const w2 = fmt2(sum - 0.01 >= 0 ? sum - 0.01 : sum + 0.1);
    const w3 = fmt2(sum + 0.1);
    const options = [correct, w1, w2, w3];
    const shuffled = [...options].sort((x, y) => {
      const h = (s: string) => s.split('').reduce((acc, c) => acc * 31 + c.charCodeAt(0), seed);
      return mulberry32(h(x))() - mulberry32(h(y))();
    });
    return {
      kind: 'mcq',
      prompt: `${fmt2(a)} + ${fmt2(b)} = ?`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(correct),
      explanation: `兩位小數加法：對齊小數點計算。${fmt2(a)} + ${fmt2(b)} = ${correct}`,
      kp: 'math.decimal.2place.4up',
    } satisfies MCQQuestion;
  } else {
    // Subtraction
    let a: number, b: number;
    if (difficulty === 1) {
      b = randInt(rand, 1, 8) / 100;
      a = randInt(rand, Math.round(b * 100) + 1, 99) / 100;
    } else if (difficulty === 2) {
      a = randInt(rand, 200, 999) / 100;
      b = randInt(rand, 100, Math.round(a * 100) - 1) / 100;
    } else {
      a = randInt(rand, 1000, 9999) / 100;
      b = randInt(rand, 500, Math.round(a * 100) - 1) / 100;
    }
    const diff = Math.round((a - b) * 100) / 100;
    const correct = fmt2(diff);
    const w1 = fmt2(diff + 0.01);
    const w2 = fmt2(diff > 0.01 ? diff - 0.01 : diff + 0.1);
    const w3 = fmt2(diff + 1);
    const options = [correct, w1, w2, w3];
    const shuffled = [...options].sort((x, y) => {
      const h = (s: string) => s.split('').reduce((acc, c) => acc * 31 + c.charCodeAt(0), seed);
      return mulberry32(h(x))() - mulberry32(h(y))();
    });
    return {
      kind: 'mcq',
      prompt: `${fmt2(a)} − ${fmt2(b)} = ?`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(correct),
      explanation: `兩位小數減法：${fmt2(a)} − ${fmt2(b)} = ${correct}`,
      kp: 'math.decimal.2place.4up',
    } satisfies MCQQuestion;
  }
}
